<template>
  <div class="modal-overlay">
    <div class="mmodal-content" style="max-width: 90vw">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="fas fa-edit"></i> Edit Patient Information
        </h5>
        <button type="button" class="close" @click="closeModal">&times;</button>
      </div>
      <div class="modal-body">
        <!-- Tabs -->
        <div class="modal-tabs">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'personal' }"
            @click="activeTab = 'personal'"
          >
            <i class="fas fa-user"></i> Personal
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'pregnancy' }"
            @click="activeTab = 'pregnancy'"
          >
            <i class="fas fa-calendar-check"></i> Pregnancy
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'obstetric' }"
            @click="activeTab = 'obstetric'"
          >
            <i class="fas fa-baby"></i> Obstetric
          </button>
        </div>

        <!-- Personal Info Tab -->
        <div v-if="activeTab === 'personal'" class="tab-content">
          <div class="form-grid">
            <div class="form-group">
              <label>Full Name *</label>
              <input v-model="patient.fullName" type="text" class="form-input" placeholder="Enter full name" />
            </div>
            <div class="form-group">
              <label>Age *</label>
              <input v-model.number="patient.age" type="number" class="form-input" placeholder="Age" min="0" max="120" />
            </div>
            <div class="form-group">
              <label>Date of Birth *</label>
              <input v-model="patient.dateOfBirth" type="date" class="form-input" />
            </div>
            <div class="form-group">
              <label>Marital Status</label>
              <select v-model="patient.maritalStatus" class="form-input">
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            </div>
            <div class="form-group">
              <label>Education</label>
              <input v-model="patient.education" type="text" class="form-input" placeholder="Education level" />
            </div>
            <div class="form-group">
              <label>Occupation</label>
              <input v-model="patient.occupation" type="text" class="form-input" placeholder="Occupation" />
            </div>
            <div class="form-group">
              <label>Husband's Name</label>
              <input v-model="patient.husbandName" type="text" class="form-input" placeholder="Husband's full name" />
            </div>
            <div class="form-group">
              <label>Husband's Occupation</label>
              <input v-model="patient.husbandOccupation" type="text" class="form-input" placeholder="Husband's occupation" />
            </div>
            <div class="form-group form-group-full">
              <label>Address *</label>
              <textarea v-model="patient.address" class="form-input" rows="2" placeholder="Full address"></textarea>
            </div>
            <div class="form-group">
              <label>Phone Number *</label>
              <input v-model="patient.phone" type="tel" class="form-input" placeholder="(555) 123-4567" />
            </div>
            <div class="form-group">
              <label>Emergency Contact *</label>
              <input v-model="patient.emergencyContact" type="text" class="form-input" placeholder="Emergency contact" />
            </div>
          </div>
        </div>

        <!-- Current Pregnancy Tab -->
        <div v-if="activeTab === 'pregnancy'" class="tab-content">
          <div class="pregnancy-edit">
            <h5 class="subsection-title">Key Dates</h5>
            <div class="form-grid">
              <div class="form-group">
                <label>Last Menstrual Period (LMP) *</label>
                <input v-model="pregnancyInfo.lmp" type="date" class="form-input" />
              </div>
              <div class="form-group">
                <label>Expected Delivery Date (EDD)</label>
                <input v-model="pregnancyInfo.edd" type="date" class="form-input" />
              </div>
              <div class="form-group">
                <label>Gestational Age (weeks)</label>
                <input v-model.number="pregnancyInfo.gestationalAge" type="number" class="form-input" placeholder="Weeks" min="0" max="45" />
              </div>
              <div class="form-group">
                <label>ANC Visits</label>
                <input v-model.number="pregnancyInfo.ancVisits" type="number" class="form-input" placeholder="Number of visits" min="0" />
              </div>
            </div>

            <h5 class="subsection-title">Lab Results & Vital Signs</h5>
            <div class="form-grid">
              <div class="form-group">
                <label>Blood Group</label>
                <select v-model="pregnancyInfo.bloodGroup" class="form-input">
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="AB">AB</option>
                  <option value="O">O</option>
                </select>
              </div>
              <div class="form-group">
                <label>Rhesus Factor</label>
                <select v-model="pregnancyInfo.rhesusFactor" class="form-input">
                  <option value="Positive">Positive</option>
                  <option value="Negative">Negative</option>
                </select>
              </div>
              <div class="form-group">
                <label>HIV Status</label>
                <select v-model="pregnancyInfo.hivStatus" class="form-input">
                  <option value="Negative">Negative</option>
                  <option value="Positive">Positive</option>
                </select>
              </div>
              <div class="form-group">
                <label>VDRL/RPR</label>
                <select v-model="pregnancyInfo.vdrl" class="form-input">
                  <option value="Non-reactive">Non-reactive</option>
                  <option value="Reactive">Reactive</option>
                </select>
              </div>
              <div class="form-group">
                <label>HB Level (g/dL)</label>
                <input v-model.number="pregnancyInfo.hbLevel" type="number" class="form-input" placeholder="Hemoglobin level" min="0" step="0.1" />
              </div>
              <div class="form-group">
                <label>Blood Pressure</label>
                <input v-model="pregnancyInfo.bloodPressure" type="text" class="form-input" placeholder="e.g., 120/80" />
              </div>
              <div class="form-group">
                <label>Weight (kg)</label>
                <input v-model.number="pregnancyInfo.weight" type="number" class="form-input" placeholder="Current weight" min="0" step="0.1" />
              </div>
              <div class="form-group">
                <label>Height (cm)</label>
                <input v-model.number="pregnancyInfo.height" type="number" class="form-input" placeholder="Height" min="0" step="0.1" />
              </div>
            </div>

            <h5 class="subsection-title">Immunization</h5>
            <div class="form-grid">
              <div class="form-group">
                <label>Tetanus Doses Completed</label>
                <input v-model.number="pregnancyInfo.tetanusCompleted" type="number" class="form-input" min="0" max="5" />
              </div>
              <div class="form-group">
                <label>Tetanus Doses Total</label>
                <input v-model.number="pregnancyInfo.tetanusTotal" type="number" class="form-input" min="0" max="5" />
              </div>
            </div>

            <h5 class="subsection-title">Appointments</h5>
            <div class="form-grid">
              <div class="form-group">
                <label>Last Visit Date</label>
                <input v-model="pregnancyInfo.lastVisit" type="date" class="form-input" />
              </div>
              <div class="form-group">
                <label>Next Appointment</label>
                <input v-model="pregnancyInfo.nextAppointment" type="date" class="form-input" />
              </div>
            </div>

            <h5 class="subsection-title">Risk Factors</h5>
            <div class="form-group form-group-full">
              <label>Medical Conditions</label>
              <textarea v-model="pregnancyInfo.medicalConditions" class="form-input" rows="2" placeholder="e.g., Hypertension, Diabetes, Anemia"></textarea>
            </div>
            <div class="form-group form-group-full">
              <label>Allergies</label>
              <textarea v-model="pregnancyInfo.allergies" class="form-input" rows="2" placeholder="e.g., Penicillin, Latex"></textarea>
            </div>
            <div class="form-group form-group-full">
              <label>Current Medications</label>
              <textarea v-model="pregnancyInfo.currentMedications" class="form-input" rows="2" placeholder="e.g., Folic Acid, Iron supplements"></textarea>
            </div>
          </div>
        </div>

        <!-- Obstetric History Tab -->
        <div v-if="activeTab === 'obstetric'" class="tab-content">
          <div class="obstetric-edit">
            <h5 class="subsection-title">Gravida/Para Summary</h5>
            <div class="gp-edit-grid">
              <div class="form-group">
                <label>Gravida (G)</label>
                <input v-model.number="obstetric.gravida" type="number" class="form-input" min="0" placeholder="0" />
              </div>
              <div class="form-group">
                <label>Para (P)</label>
                <input v-model.number="obstetric.para" type="number" class="form-input" min="0" placeholder="0" />
              </div>
              <div class="form-group">
                <label>Abortions (A)</label>
                <input v-model.number="obstetric.abortions" type="number" class="form-input" min="0" placeholder="0" />
              </div>
              <div class="form-group">
                <label>Living Children (L)</label>
                <input v-model.number="obstetric.livingChildren" type="number" class="form-input" min="0" placeholder="0" />
              </div>
            </div>

            <h5 class="subsection-title">
              Previous Pregnancies
              <button class="btn-add-pregnancy" @click="addPregnancy">
                <i class="fas fa-plus"></i> Add
              </button>
            </h5>

            <div v-if="obstetric.previousPregnancies.length === 0" class="no-pregnancies">
              <i class="fas fa-info-circle"></i>
              No previous pregnancies recorded
            </div>

            <div v-for="(pregnancy, index) in obstetric.previousPregnancies" :key="index" class="pregnancy-entry">
              <div class="pregnancy-header">
                <span class="pregnancy-number">Pregnancy #{{ index + 1 }}</span>
                <button class="btn-remove" @click="removePregnancy(index)" title="Remove">
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
              <div class="pregnancy-grid">
                <div class="form-group">
                  <label>Year *</label>
                  <input v-model.number="pregnancy.year" type="number" class="form-input" placeholder="Year" />
                </div>
                <div class="form-group">
                  <label>Outcome *</label>
                  <select v-model="pregnancy.outcome" class="form-input">
                    <option value="Live Birth">Live Birth</option>
                    <option value="Stillbirth">Stillbirth</option>
                    <option value="Miscarriage">Miscarriage</option>
                    <option value="Abortion">Abortion</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Gestation (wks)</label>
                  <input v-model.number="pregnancy.gestation" type="number" class="form-input" placeholder="Weeks" />
                </div>
                <div class="form-group">
                  <label>Birth Weight (kg)</label>
                  <input v-model="pregnancy.birthWeight" type="text" class="form-input" placeholder="Weight" />
                </div>
                <div class="form-group">
                  <label>Delivery Mode</label>
                  <select v-model="pregnancy.deliveryMode" class="form-input">
                    <option value="Vaginal">Vaginal</option>
                    <option value="Cesarean Section">Cesarean Section</option>
                    <option value="Vacuum Assisted">Vacuum Assisted</option>
                    <option value="Forceps">Forceps</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Complications</label>
                  <div class="checkbox-group">
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="pregnancy.complications" />
                      <span>Had complications</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" @click="closeModal">
          Cancel
        </button>
        <button type="button" class="btn btn-primary" @click="saveModal">
          Save Changes
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "EditPatientModal",
  emits: ["close-edit-patient", "save"],
  data() {
    return {
      activeTab: "personal",
      patient: {
        fullName: "Sarah Johnson",
        age: 32,
        dateOfBirth: "1991-05-15",
        maritalStatus: "Married",
        education: "Bachelor's Degree",
        occupation: "Teacher",
        husbandName: "Michael Johnson",
        husbandOccupation: "Engineer",
        address: "123 Main Street, Springfield, IL 62704",
        phone: "(555) 123-4567",
        emergencyContact: "(555) 987-6543 (Husband)",
      },
      pregnancyInfo: {
        lmp: "2023-06-15",
        edd: "2024-03-22",
        gestationalAge: 28,
        ancVisits: 5,
        bloodGroup: "O",
        rhesusFactor: "Positive",
        hivStatus: "Negative",
        vdrl: "Non-reactive",
        hbLevel: 11.5,
        bloodPressure: "120/80",
        weight: 68.5,
        height: 165,
        tetanusCompleted: 2,
        tetanusTotal: 5,
        lastVisit: "2023-11-15",
        nextAppointment: "2023-12-15",
        medicalConditions: "",
        allergies: "",
        currentMedications: "Folic Acid, Iron supplements",
      },
      obstetric: {
        gravida: 3,
        para: 2,
        abortions: 0,
        livingChildren: 2,
        previousPregnancies: [
          {
            year: 2020,
            outcome: "Live Birth",
            gestation: 39,
            birthWeight: "3.2",
            deliveryMode: "Cesarean Section",
            complications: true,
          },
          {
            year: 2022,
            outcome: "Live Birth",
            gestation: 40,
            birthWeight: "3.5",
            deliveryMode: "Vaginal",
            complications: false,
          },
        ],
      },
    };
  },
  methods: {
    closeModal() {
      this.$emit("close-edit-patient");
    },
    saveModal() {
      if (!this.patient.fullName || !this.patient.dateOfBirth || !this.patient.phone || !this.patient.address) {
        alert("Please fill in all required fields (marked with *)");
        return;
      }

      if (!this.pregnancyInfo.lmp) {
        alert("Please enter Last Menstrual Period date");
        return;
      }

      if (this.obstetric.previousPregnancies.length > 0) {
        for (let pregnancy of this.obstetric.previousPregnancies) {
          if (!pregnancy.year || !pregnancy.outcome) {
            alert("Please fill in year and outcome for all pregnancies");
            return;
          }
        }
      }

      this.$emit("save", {
        patient: this.patient,
        pregnancyInfo: this.pregnancyInfo,
        obstetric: this.obstetric,
      });
    },
    addPregnancy() {
      this.obstetric.previousPregnancies.push({
        year: new Date().getFullYear(),
        outcome: "Live Birth",
        gestation: null,
        birthWeight: "",
        deliveryMode: "Vaginal",
        complications: false,
      });
    },
    removePregnancy(index) {
      if (confirm("Are you sure you want to remove this pregnancy record?")) {
        this.obstetric.previousPregnancies.splice(index, 1);
      }
    },
  },
};
</script>

<style scoped>
.modal-body {
  padding: 1rem;
  overflow-y: auto;
  flex-grow: 1;
  max-height: 95vh;
}

.modal-footer {
  border-top: 1px solid #ddd;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 1rem;
}

.close {
  background: rgb(233, 22, 22);
  border: 1px solid rgb(182, 178, 178);
  font-size: 1.5rem;
  color: white;
  line-height: 1;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 4px;
}

.close:hover {
  background: rgb(200, 18, 18);
}

/* Tabs */
.modal-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 1rem;
  padding-bottom: 0.8rem;
  border-bottom: 2px solid #f1f3f4;
}

.tab-btn {
  flex: 1;
  padding: 10px 16px;
  border: 2px solid #e9ecef;
  background: white;
  color: #6c757d;
  font-weight: 600;
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.tab-btn:hover {
  border-color: #667eea;
  color: #667eea;
  background: #f8f9fe;
}

.tab-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-color: #667eea;
}

.tab-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Form Styles */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

.form-group {
  margin-bottom: 2px;
}

.form-group-full {
  grid-column: 1 / -1;
}

.form-group label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.form-input {
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #dee2e6;
  border-radius: 6px;
  font-size: 13px;
  color: #212529;
  transition: all 0.2s ease;
  background: white;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

textarea.form-input {
  resize: vertical;
  min-height: 60px;
}

select.form-input {
  cursor: pointer;
}

/* Buttons */
.btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: #e9ecef;
  color: #495057;
  border: 1px solid #dee2e6;
}

.btn-secondary:hover {
  background: #dee2e6;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
}

/* Section Titles */
.subsection-title {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  margin: 16px 0 10px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f1f3f4;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.subsection-title:first-child {
  margin-top: 0;
}

/* GP Edit */
.gp-edit-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  background: #f8f9fa;
  padding: 14px;
  border-radius: 8px;
}

/* Add Pregnancy Button */
.btn-add-pregnancy {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: none;
  padding: 5px 12px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s ease;
}

.btn-add-pregnancy:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(40, 167, 69, 0.3);
}

/* No Pregnancies */
.no-pregnancies {
  text-align: center;
  padding: 25px;
  background: #f8f9fa;
  border-radius: 8px;
  color: #6c757d;
  font-size: 13px;
}

.no-pregnancies i {
  display: block;
  font-size: 22px;
  margin-bottom: 8px;
  color: #adb5bd;
}

/* Pregnancy Entry */
.pregnancy-entry {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 10px;
}

.pregnancy-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.pregnancy-number {
  font-size: 12px;
  font-weight: 600;
  color: #667eea;
  text-transform: uppercase;
}

.btn-remove {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 3px 8px;
  border-radius: 4px;
  transition: all 0.2s;
  font-size: 12px;
}

.btn-remove:hover {
  background: #dc3545;
  color: white;
}

.pregnancy-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

/* Checkbox */
.checkbox-group {
  padding-top: 8px;
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #667eea;
}

/* Modal Title */
.modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-title i {
  color: #667eea;
}

/* Responsive */
@media (max-width: 992px) {
  .mmodal-content {
    max-width: 95vw !important;
  }
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .gp-edit-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .pregnancy-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .mmodal-content {
    max-width: 100vw !important;
  }

  .modal-tabs {
    flex-direction: column;
    gap: 5px;
  }

  .pregnancy-grid {
    grid-template-columns: 1fr;
  }

  .modal-footer {
    flex-direction: column-reverse;
  }

  .btn {
    width: 100%;
  }
}
</style>